import React, { ReactNode } from "react";
import DragScroll from "@/components/DragScroll/DragScroll";

interface ScrollSnapRowProps {
  children: ReactNode;
  /** Wrapper class. */
  className?: string;
  /** Class on the scrolling track (gaps, padding). Default gives a 24px gap. */
  trackClassName?: string;
  /** Class on each slide cell (width control, e.g. shrink-0 widths). */
  itemClassName?: string;
  /** "mandatory" snaps hard to each slide; "proximity" is a softer free-scroll feel; "none" = free. */
  snap?: "mandatory" | "proximity" | "none";
}

// A dependency-free horizontal carousel: native overflow-x scroll + CSS scroll-snap, replacing
// Swiper for simple "swipe through cards" rows. It ships ZERO carousel JS — the browser handles
// touch/drag/momentum, and flexbox equalizes slide heights. The slides stay in the server-rendered
// DOM (no SEO loss). Used on the home page so Swiper (~33 KB) leaves the mobile critical path,
// which Lighthouse (simulate/mobile) showed is what gates LCP/Speed Index.
export default function ScrollSnapRow({
  children,
  className = "",
  trackClassName = "gap-6",
  itemClassName = "",
  snap = "proximity",
}: ScrollSnapRowProps) {
  const snapTrack = snap === "none" ? "" : snap === "mandatory" ? "snap-x snap-mandatory" : "snap-x snap-proximity";
  const snapItem = snap === "none" ? "" : "snap-start";
  return (
    <div className={`relative ${className}`}>
      <DragScroll
        className={`flex items-stretch overflow-x-auto overflow-y-hidden ${snapTrack} [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${trackClassName}`}
      >
        {React.Children.map(children, (child, idx) => (
          <div key={idx} className={`shrink-0 ${snapItem} ${itemClassName}`}>
            {child}
          </div>
        ))}
      </DragScroll>
    </div>
  );
}
